import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "Shared/util/custom-hooks";
import {
    ConsentStatusResource,
    PaymentInformationMessageResource,
} from "Shared/util/types";
import FadeTransition from "Shared/components/transitions/fade-transition";
import TextLink from "Shared/components/text-link";
import { useConsentStatusPoller } from "../hooks";
import styles from "./decoupled-other-device-tab.css";
import { DecoupledOtherDeviceTabProps } from "./types";

export const DecoupledOtherDeviceTab = ({
    data: { token, qrCodeImageUrl },
    onError,
    onReturn,
    onUserVerify,
    show,
}: DecoupledOtherDeviceTabProps) => {
    const t = useTranslation((x) => x.edit_payment_information);

    const [_qrCodeImageUrl, setQrCodeImageUrl] = useState("");
    const { decoupled_other_device_tab: ttab } = t;

    useEffect(() => {
        if (qrCodeImageUrl) {
            setQrCodeImageUrl(qrCodeImageUrl);
        }
    }, [qrCodeImageUrl]);

    const handlePollFailedStatus = useCallback(
        (message: PaymentInformationMessageResource | null) =>
            onError(true, message),
        [onError],
    );

    const handlePollFetchError = useCallback(
        (message: PaymentInformationMessageResource | null) =>
            onError(true, message),
        [onError],
    );

    const handlePollStatusUpdate = useCallback(
        (consentResource: ConsentStatusResource) =>
            consentResource?.challengeData &&
            setQrCodeImageUrl(consentResource.challengeData),
        [],
    );

    const {
        begin: beginConsentPoller,
        disable: disableConsentPoller,
        enable: enableConsentPoller,
    } = useConsentStatusPoller(
        onUserVerify,
        handlePollFailedStatus,
        handlePollFetchError,
        handlePollStatusUpdate,
    );

    useEffect(() => {
        if (!show || !token) return;

        enableConsentPoller();
        beginConsentPoller(token);
    }, [beginConsentPoller, enableConsentPoller, show, token]);

    const handleReturn = () => {
        disableConsentPoller();
        onReturn(token);
    };

    return (
        <FadeTransition
            isOpen={show}
            enterDuration={200}
            className={styles.container}
        >
            {!show ? null : (
                <>
                    <h3>{t.identify_with_bankid}</h3>
                    <p>{ttab.decoupled_flow_instructions}</p>
                    <div className={styles.qrCodeContainer}>
                        <img src={_qrCodeImageUrl} alt="QR Code" />
                    </div>
                    <TextLink
                        text={t.return}
                        link={handleReturn}
                        className={styles.returnTextLink}
                    />
                </>
            )}
        </FadeTransition>
    );
};
